import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { opsApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { ModelPrompt, ModelPrompts } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';

/** The first message of a `{text: [message]}` answer, which is how a refused text is reported. */
function refusal(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: Record<string, unknown> } })?.response?.data;
  for (const field of ['text', 'detail']) {
    const value = data?.[field];
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    if (typeof value === 'string') return value;
  }
  return errorMessage(e, fallback);
}

/**
 * The Prompts section of the staff area: the instructions each model step is
 * given when a resume is tailored. Each has a built-in text; the owner can
 * use their own instead, which is stored in AWS Secrets Manager and takes
 * effect without a deploy.
 */
const PromptsPage: React.FC = () => {
  const { show, toast } = useToast();
  const [data, setData] = useState<ModelPrompts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState<ModelPrompt | null>(null);
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await opsApi.prompts());
      setError(null);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setForbidden(true);
      else setError(errorMessage(e, 'Could not load the instructions. Please try again.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (prompt: ModelPrompt) => {
    setEditing(prompt);
    setDraft(prompt.text);
    setProblem(null);
  };

  const finish = (updated: ModelPrompt, message: string) => {
    setData((current) => (current ? { ...current, prompts: current.prompts.map((p) => (p.name === updated.name ? updated : p)) } : current));
    setEditing(null);
    show(message);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setProblem(null);
    try {
      finish(await opsApi.savePrompt(editing.name, draft), 'Saved. New tailored resumes follow it within five minutes.');
    } catch (e) {
      setProblem(refusal(e, 'The text could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!editing) return;
    setSaving(true);
    setProblem(null);
    try {
      finish(await opsApi.resetPrompt(editing.name), 'Back to the built-in text.');
    } catch (e) {
      setProblem(refusal(e, 'It could not be reset.'));
    } finally {
      setSaving(false);
    }
  };

  if (forbidden) {
    return <Alert severity="info">Only the owner of this JobFlo installation can see or change the instructions its models follow.</Alert>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {!data ? (
        !error && <Skeleton variant="rounded" height={260} />
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            What each model step is told when a resume is tailored. Your own text replaces the built-in one, is stored in AWS
            Secrets Manager (jobflo/prod/prompts) and is used within five minutes; after a change, a tailored resume for a
            posting is built afresh. The fact check that puts back unsupported lines is code, not instructions, and stays on
            whatever is written here.
          </Typography>
          {!data.available && (
            <Alert severity="info">This server has no prompts secret, so the built-in texts are in use and cannot be changed here.</Alert>
          )}
          {data.prompts.map((prompt) => (
            <Paper key={prompt.name} variant="outlined" component="section" aria-label={prompt.label} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 0.5 }}>
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                  {prompt.label}
                </Typography>
                <Chip size="small" variant="outlined" color={prompt.custom ? 'primary' : 'default'} label={prompt.custom ? 'Your own' : 'Built-in'} />
                <Box sx={{ flex: 1 }} />
                <Button size="small" disabled={!data.available} onClick={() => open(prompt)}>
                  Edit
                </Button>
              </Stack>
              {prompt.ignored_because && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  A stored text is not being used: {prompt.ignored_because}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {prompt.changed_at ? `Last changed ${formatDateTime(prompt.changed_at)}${prompt.changed_by ? ` by ${prompt.changed_by}` : ''}` : 'Never changed here'}
              </Typography>
              <Box
                component="pre"
                tabIndex={0}
                aria-label={`${prompt.label}: the text in use`}
                sx={{ m: 0, p: 1.5, maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              >
                {prompt.text}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={editing !== null} onClose={saving ? undefined : () => setEditing(null)} fullWidth maxWidth="md" aria-labelledby="prompt-dialog-title">
        <DialogTitle id="prompt-dialog-title">{editing?.label}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {problem && <Alert severity="error">{problem}</Alert>}
            <Typography variant="body2" color="text.secondary">
              Keep the part that describes the JSON answer: the code reads that shape, and a text without it is refused.
            </Typography>
            <TextField
              label="Instructions"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              multiline
              minRows={16}
              maxRows={28}
              fullWidth
              inputProps={{ spellCheck: false, style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.5 } }}
              helperText={`${draft.length.toLocaleString()} characters`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button color="inherit" disabled={saving || draft === editing?.builtin_text} onClick={() => setDraft(editing?.builtin_text ?? '')}>
            Load the built-in text
          </Button>
          {editing?.custom && (
            <Button color="inherit" disabled={saving} onClick={reset}>
              Use the built-in
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" disabled={saving || !draft.trim() || draft.trim() === editing?.text.trim()} onClick={save}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {toast}
    </Box>
  );
};

export default PromptsPage;
